import React, { FC, useContext, useCallback, useEffect } from 'react'
import { TextInput, TextInputProps } from 'react-native'
import { useField, useFormikContext, FormikContextType } from 'formik'

import { Element } from '../Element'
import { Stack } from '../Stack'
import { FormFieldContext } from './FormField'

export interface FormTextInputProps {
  keyboardType?: TextInputProps['keyboardType']
  multiline?: TextInputProps['multiline']
  numberOfLines?: TextInputProps['numberOfLines']
  onChange?: (value: string, actions: FormikContextType<unknown>) => any
  placeholder?: TextInputProps['placeholder']
  size?: number
  submitBehaviour?: 'submit-form'
  variant?: string
  autoFocus?: boolean
  secureTextEntry?: boolean
  passwordRules?: string
  spellCheck?: boolean
  autoCapitalize?: TextInputProps['autoCapitalize']
  autoCorrect?: TextInputProps['autoCorrect']
}

export const FormTextInput: FC<FormTextInputProps> = ({
  keyboardType,
  multiline,
  numberOfLines,
  onChange,
  placeholder,
  size = 5,
  submitBehaviour,
  variant,
  autoFocus,
  secureTextEntry,
  passwordRules,
  spellCheck,
  autoCapitalize,
  autoCorrect,
}) => {
  const formContext = useFormikContext()
  const formFieldContext = useContext(FormFieldContext)
  const [field, _meta, helpers] = useField(formFieldContext.name)

  const onSubmitEditing = useCallback(() => {
    if (!submitBehaviour) {
      return
    }

    if (submitBehaviour === 'submit-form') {
      formContext.submitForm()
    }
  }, [formContext, submitBehaviour])

  useEffect(() => {
    const element = formFieldContext.formElementRef
      .current as HTMLInputElement | null

    if (element) {
      if (formFieldContext.name) {
        element.setAttribute('name', formFieldContext.name)
      }

      if (secureTextEntry && passwordRules) {
        element.setAttribute('passwordrules', passwordRules)
      }
    }
  }, [formFieldContext.name, secureTextEntry, passwordRules])

  return (
    <Element
      as={TextInput}
      tokens={{ fontSize: size, lineHeight: size }}
      ref={formFieldContext.formElementRef}
      placeholder={placeholder}
      placeholderTextColor='#9A97A8'
      onChangeText={(value) => {
        helpers.setValue(value)

        if (onChange) {
          onChange(value, formContext)
        }
      }}
      component='FormTextInput'
      variant={variant}
      keyboardType={keyboardType}
      multiline={multiline}
      numberOfLines={numberOfLines}
      onSubmitEditing={onSubmitEditing}
      value={field.value}
      onFocus={formFieldContext.onFocus}
      onBlur={formFieldContext.onBlur}
      autoFocus={autoFocus}
      secureTextEntry={secureTextEntry}
      spellCheck={spellCheck}
      autoCapitalize={autoCapitalize}
      autoCorrect={autoCorrect}
    />
  )
}
